/*
 * Avviso "spediamo quando il magazzino e completo".
 *
 * Liquid non espone le giacenze per magazzino: l'unico campo per location della
 * Storefront API e ProductVariant.storeAvailability, che pero elenca solo le
 * location con ritiro locale attivo. Su questo shop entrambe le location che
 * vendono online ce l'hanno, quindi il dato arriva.
 *
 * Regola: mostra l'avviso quando il magazzino che spedisce non copre da solo la
 * quantita richiesta, ma qualcosa ce l'ha (0 < readyQty < richiesta). Se e a zero
 * l'articolo e interamente in arrivo e resta il badge restock/preorder gia esistente.
 */
class CartSplitStock {
    constructor(config) {
        this.token = config.token;
        this.readyLocationGid = `gid://shopify/Location/${config.readyLocationId}`;
        this.apiVersion = config.apiVersion || '2025-07';
        this.readyQtyCache = new Map();
        this.pending = null;

        this.refresh();

        // updateCart() rigenera l'HTML del carrello da Liquid: i badge tornano
        // nascosti a ogni cambio quantita e vanno ricalcolati.
        document.addEventListener('cart-update', () => this.scheduleRefresh());
    }

    scheduleRefresh() {
        clearTimeout(this.pending);
        this.pending = setTimeout(() => this.refresh(), 100);
    }

    // Due refresh ravvicinati partirebbero entrambi prima che la cache sia popolata,
    // duplicando la chiamata: li mettiamo in coda.
    refresh() {
        this.inFlight = (this.inFlight || Promise.resolve())
            .catch(() => {})
            .then(() => this.applyBadges());
        return this.inFlight;
    }

    async applyBadges() {
        const badges = Array.from(document.querySelectorAll('[data-split-stock-badge]'));
        if (!badges.length) return;

        // Lo stesso articolo puo comparire su piu righe (proprieta diverse):
        // conta quanti pezzi servono in totale, non riga per riga.
        const requestedByVariant = new Map();
        badges.forEach((badge) => {
            const id = badge.dataset.splitVariantId;
            const qty = parseInt(badge.dataset.splitQty, 10) || 0;
            requestedByVariant.set(id, (requestedByVariant.get(id) || 0) + qty);
        });

        const missing = Array.from(requestedByVariant.keys()).filter((id) => !this.readyQtyCache.has(id));
        if (missing.length) {
            const fetched = await this.fetchReadyQuantities(missing);
            if (!fetched) return;
        }

        badges.forEach((badge) => {
            const id = badge.dataset.splitVariantId;
            const readyQty = this.readyQtyCache.get(id);
            const requested = requestedByVariant.get(id);

            if (readyQty === undefined || readyQty === null) return;
            badge.hidden = !(readyQty > 0 && requested > readyQty);
        });
    }

    async fetchReadyQuantities(variantIds) {
        const query = `query CartSplitStock($ids: [ID!]!) {
            nodes(ids: $ids) {
                ... on ProductVariant {
                    id
                    storeAvailability(first: 10) {
                        nodes {
                            quantityAvailable
                            location { id }
                        }
                    }
                }
            }
        }`;

        try {
            const response = await fetch(`/api/${this.apiVersion}/graphql.json`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': this.token,
                },
                body: JSON.stringify({
                    query,
                    variables: { ids: variantIds.map((id) => `gid://shopify/ProductVariant/${id}`) },
                }),
            });

            if (!response.ok) return false;

            const payload = await response.json();
            const nodes = payload?.data?.nodes;
            if (!Array.isArray(nodes)) return false;

            nodes.forEach((node) => {
                if (!node?.id) return;
                const variantId = node.id.split('/').pop();
                const locations = node.storeAvailability?.nodes || [];

                // Vuoto = prodotto non spedibile (requiresShipping false): niente ritiro
                // locale, quindi niente dato per magazzino. Non ci riguarda.
                if (!locations.length) {
                    this.readyQtyCache.set(variantId, null);
                    return;
                }

                const ready = locations.find((entry) => entry.location?.id === this.readyLocationGid);
                this.readyQtyCache.set(variantId, ready ? ready.quantityAvailable : null);
            });

            return true;
        } catch (error) {
            // Token mancante, rete giu, scope revocati: il carrello resta com'e.
            return false;
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const holder = document.querySelector('[data-split-stock-config]');
    if (!holder || !holder.dataset.token) return;

    new CartSplitStock({
        token: holder.dataset.token,
        readyLocationId: holder.dataset.readyLocationId,
        apiVersion: holder.dataset.apiVersion,
    });
});
